import { Directive, OnDestroy } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Params, Router } from '@angular/router';
import { LoadingMetadata } from '@app/shared/store/standard-entity.state';
import { routeParamsFromRoute } from '@app/shared/utils/route-utils';
import { State } from '@app/store/reducers';
import {
  Action,
  ActionCreator,
  Creator,
  MemoizedSelector,
  MemoizedSelectorWithProps,
  select,
  Store,
} from '@ngrx/store';
import { DateTime, Duration, DurationLikeObject } from 'luxon';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';

export type UrlPathSegment = string | { [paramName: string]: string | number };

/**
 * Contains common functions for interacting with the store and router
 */
@Directive()
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export abstract class StandardContainerComponent implements OnDestroy {
  protected requested: { [key: string]: boolean } = {};

  protected destroyed$ = new Subject<void>();

  protected constructor(
    protected store: Store<State>,
    protected route: ActivatedRoute,
    protected router: Router,
  ) {}

  ngOnDestroy() {
    this.destroyed$.next();
  }

  protected flattenRouteParams(
    snapshot: ActivatedRouteSnapshot,
    params: Params = {},
    depth = 0,
  ): Params {
    if (depth === 0 && snapshot !== snapshot.root) {
      return this.flattenRouteParams(snapshot.root);
    }

    Object.getOwnPropertyNames(snapshot.params).forEach((key) => {
      params[key] = snapshot.params[key];
    });

    if (snapshot.firstChild) {
      return this.flattenRouteParams(snapshot.firstChild, params, depth + 1);
    }

    return params;
  }

  protected buildChildPath(route: ActivatedRoute): UrlPathSegment[] {
    const childPathSegments = [];

    let child = route.snapshot.firstChild;
    while (child) {
      child.url.forEach((urlSegment) => {
        childPathSegments.push(urlSegment.path);
        if (Object.getOwnPropertyNames(urlSegment.parameters).length > 0) {
          childPathSegments.push(urlSegment.parameters);
        }
      });

      child = child.firstChild;
    }

    return [...childPathSegments];
  }

  protected removeCurrentRouteParameters(paramNames: string[]): void {
    let currentSegmentIndex = -1;
    let node = this.route.snapshot;
    while (node != null) {
      currentSegmentIndex += node.url.length;
      node = node.parent;
    }

    const urlTree = this.router.parseUrl(this.router.url);
    const segments = urlTree.root.children.primary.segments;
    const segment = segments[currentSegmentIndex];
    for (const paramName of paramNames) {
      if (segment.parameters[paramName]) {
        delete segment.parameters[paramName];
      }
    }

    this.router.navigateByUrl(this.router.serializeUrl(urlTree));
  }

  /**
   * If a key is present in @param params, it will be set in the route as follows:
   *   - if nullish, the parameter will be removed from the route
   *   - otherwise, the parameter value will be set in the route
   *
   * Existing route parameters whose key is not present in @param params will be left alone.
   */
  protected setCurrentRouteParameters(params: {[key: string]: string }): void {
    let currentSegmentIndex = -1;
    let node = this.route.snapshot;
    while (node != null) {
      currentSegmentIndex += node.url.length;
      node = node.parent;
    }

    const urlTree = this.router.parseUrl(this.router.url);
    const segments = urlTree.root.children.primary.segments;
    const segment = segments[currentSegmentIndex];

    for (const key in params) {
      if (params[key] == null) {
        if (segment.parameters[key] != null) {
          delete segment.parameters[key];
        }
        delete params[key];
      }
    }

    Object.assign(segment.parameters, params);
    this.router.navigateByUrl(this.router.serializeUrl(urlTree));
  }

  protected setCurrentRouteParameter(paramName: string, paramValue: string): void {
    let currentSegmentIndex = -1;
    let node = this.route.snapshot;
    while (node != null) {
      currentSegmentIndex += node.url.length;
      node = node.parent;
    }

    const param = {};
    param[paramName] = paramValue;

    const urlTree = this.router.parseUrl(this.router.url);
    const segments = urlTree.root.children.primary.segments;
    const segment = segments[currentSegmentIndex];
    Object.assign(segment.parameters, param);
    this.router.navigateByUrl(this.router.serializeUrl(urlTree));
  }

  protected selectIfNotLoadedAndNotRequested<S>(
    // eslint-disable-next-line @typescript-eslint/ban-types
    selector: MemoizedSelector<object, S>,
    // eslint-disable-next-line @typescript-eslint/ban-types
    loadedSelector: MemoizedSelector<object, boolean>,
    key: string,
    action: Action,
  ): Observable<S> {
    return this.store.pipe(
      select(selector),
      withLatestFrom(this.store.pipe(select(loadedSelector))),
      map(([obj, loaded]) => {
        if (this.requested[key] == null && loaded === false) {
          this.store.dispatch(action);
          this.requested[key] = true;
        }
        return obj;
      }),
    );
  }

  protected selectWithPropsIfNotLoadedAndNotRequested<S, R>(
    // eslint-disable-next-line @typescript-eslint/ban-types
    selector: MemoizedSelectorWithProps<object, R, S>,
    props: R,
    // eslint-disable-next-line @typescript-eslint/ban-types
    loadedSelector: MemoizedSelector<object, boolean>,
    key: string,
    action: Action,
  ): Observable<S> {
    return this.store.pipe(
      select(selector, props),
      withLatestFrom(this.store.pipe(select(loadedSelector))),
      map(([obj, loaded]) => {
        if (this.requested[key] == null && loaded === false) {
          this.store.dispatch(action);
          this.requested[key] = true;
        }
        return obj;
      }),
    );
  }

  protected loadIfNotLoadedOrOlderThanDefaultMaxAge<S extends State>(
    loadedMetadataSelect: (source$: Observable<S>) => Observable<LoadingMetadata>,
    loadAction: Action,
  ) {
    this.loadIfNotLoadedOrOlderThan(loadedMetadataSelect, loadAction);
  }

  protected loadIfNotLoadedOrOlderThan<S extends State>(
    loadedMetadataSelect: (source$: Observable<S>) => Observable<LoadingMetadata>,
    loadAction: Action,
    maxAge?: Duration | DurationLikeObject,
  ) {
    this.store
      .pipe(
        loadedMetadataSelect,
        take(1),
        tap((loadedMetadata) => {
          const { loadingAt, loadedAt, defaultMaxAge } = loadedMetadata;
          maxAge =
            maxAge != null
              ? Duration.isDuration(maxAge)
                ? maxAge
                : Duration.fromObject(maxAge)
              : defaultMaxAge;
          if (
            (!loadingAt || DateTime.local().diff(loadingAt).valueOf() > 5000) &&
            (maxAge.valueOf() === 0 ||
              !loadedAt ||
              !DateTime.isDateTime(loadedAt) ||
              !loadedAt.isValid ||
              DateTime.local().diff(loadedAt) > maxAge)
          ) {
            this.store.dispatch(loadAction);
          }
        }),
      )
      .subscribe();
  }

  /**
   * Returns an Observable from the Store based on @param dataSelect.
   *
   * As a side effect, it also dispatches @param action to the Store
   * in the following circumstances:
   * - the data is not present in the Store
   * - it has been longer than defaultMaxAge since the data was loaded into the Store
   * unless an attempt to load the data was initiated within the last 5 seconds.
   */
  protected selectAndLoad<S extends State, T>(
    dataSelect: (source$: Observable<S>) => Observable<T>,
    loadedMetadataSelect: (source$: Observable<S>) => Observable<LoadingMetadata>,
    action: Action,
  ): Observable<T> {
    return this.selectAndLoadIfOlderThan(dataSelect, loadedMetadataSelect, action);
  }

  /**
   * Returns an Observable from the Store based on @param dataSelect.
   *
   * As a side effect, it also dispatches @param action to the Store
   * in the following circumstances:
   * - the data is not present in the Store
   * - it has been longer than defaultMaxAge since the data was loaded into the Store
   * - it has been longer than @param maxAge since the data was loaded into the Store
   * unless an attempt to load the data was initiated within the last 5 seconds.
   *
   * Eventually, this should replace both selectIfNotLoadedAndNotRequested and
   * selectWithPropsIfNotLoadedAndNotRequested.
   */
  protected selectAndLoadIfOlderThan<S extends State, T>(
    dataSelect: (source$: Observable<S>) => Observable<T>,
    loadedMetadataSelect: (source$: Observable<S>) => Observable<LoadingMetadata>,
    action: Action,
    maxAge?: Duration | DurationLikeObject,
  ): Observable<T> {
    return this.store.pipe(
      takeUntil(this.destroyed$),
      dataSelect,
      withLatestFrom(this.store.pipe(loadedMetadataSelect)),
      map(([obj, loadedMetadata]) => {
        const loadingAt = loadedMetadata?.loadingAt;
        const loadedAt = loadedMetadata?.loadedAt;
        maxAge =
          maxAge != null
            ? Duration.isDuration(maxAge)
              ? maxAge
              : Duration.fromObject(maxAge)
            : loadedMetadata?.defaultMaxAge;
        if (
          (!loadingAt || DateTime.local().diff(loadingAt).valueOf() > 5000) &&
          (maxAge.valueOf() === 0 ||
            !loadedAt ||
            !DateTime.isDateTime(loadedAt) ||
            !loadedAt.isValid ||
            DateTime.local().diff(loadedAt) > maxAge)
        ) {
          this.store.dispatch(action);
        }

        return obj;
      }),
    );
  }

  protected selectIfNotLoadedAndNotRequestedWithArgs<S, R, T extends string, U extends Creator>(
    // eslint-disable-next-line @typescript-eslint/ban-types
    selector: MemoizedSelector<object, S>,
    // eslint-disable-next-line @typescript-eslint/ban-types
    loadedSelector: MemoizedSelector<object, boolean>,
    key: string,
    actionCreator: ActionCreator<T, U>,
    // eslint-disable-next-line @typescript-eslint/ban-types
    argsSelector: MemoizedSelector<object, R>,
  ): Observable<S> {
    return combineLatest([
      this.store.pipe(select(selector)),
      this.store.pipe(select(loadedSelector)),
      this.store.pipe(select(argsSelector)),
    ]).pipe(
      map(([obj, loaded, args]) => {
        if (this.requested[key] == null && loaded === false && args != null) {
          this.store.dispatch(actionCreator(args) as Action);
          this.requested[key] = true;
        }
        return obj;
      }),
    );
  }

  protected getFirstRouteParam<T>(paramName: string): Observable<T> {
    return routeParamsFromRoute(this.route).pipe(
      takeUntil(this.destroyed$),
      map((paramsArray) => {
        const params = paramsArray.find(p => !!p[paramName]);

        if (params) {
          return params[paramName];
        }

        return params;
      }),
      map((param) => {
        if (param == null || isNaN(+param)) {
          return param;
        }

        return +param;
      }),
    );
  }
}
